"use client";

import { useEffect, useMemo, useRef } from "react";
import { useStudio } from "@/contexts/studio-context";
import {
  SequenceMatcher,
  type SequenceGroup,
} from "@/lib/learning-journey/sequence-matcher";

/**
 * Watches the live piano for an ordered sequence of groups (a group =
 * one note or a chord). Used by S1b/S2b/S3 of the learning journey.
 *
 * The component is a thin React adapter over `SequenceMatcher` (pure TS,
 * tested in Jest). It subscribes to `Piano.press` events, feeds them
 * into the matcher, and forwards outcomes through callback props.
 *
 * Behaviour on a wrong note (design choice)
 * -----------------------------------------
 *
 * `SequenceMatcher` reports wrong presses without resetting the matcher
 *, see `sequence-matcher.ts` for the rationale. The watcher mirrors
 * that policy: it calls `onError` and keeps going. The player can
 * "wander" on the keyboard and still complete the sequence.
 *
 * Held keys
 * ---------
 *
 * A group is accepted on the keys that are **down**, not on how fast
 * they were struck, so the watcher tracks the held set: it seeds it from
 * `Piano.getPressedMidis()` at subscribe time, then keeps it with the
 * press/release events. Seeding matters on a retry — the watcher is
 * remounted while the player may still be holding the last chord, and a
 * set starting empty would silently forget those keys.
 *
 * Returns nothing visible
 * -----------------------
 *
 * The watcher does not render any UI. Visual feedback (current note,
 * progress, errors, hints on the piano-roll) lives in the state views
 * (`discover-state`, `practice-state`) so the watcher stays a one-job
 * primitive, exactly like `NoteWatcher` is for single notes, except
 * that the existing `NoteWatcher` *does* render a `NoteCard`. Here we
 * do not couple the watcher to a particular visual; the parent decides.
 */

export type SequenceWatcherProps = {
  /** Ordered groups to validate. Pass a stable reference per sequence. */
  readonly groups: readonly SequenceGroup[];
  /** Fires once per group accepted. The `index` is the just-validated one. */
  readonly onGroupConfirmed?: (index: number) => void;
  /** Fires after the last group is validated. */
  readonly onSequenceCompleted?: () => void;
  /** Fires for each press that does not match the current group. */
  readonly onError?: (midi: number) => void;
  /**
   * Optional progress hook surfaced to the parent. The watcher emits
   * `nextIndex` whenever it changes (initial mount + after each
   * confirmation). Used by the state views to render "note 3/12".
   */
  readonly onCurrentIndexChange?: (currentIndex: number) => void;
  /**
   * Group the sequence is taken up at, `0` for its beginning.
   *
   * Where the player scrolled the roll to. Must be a value that only changes
   * when they ask for it: it recreates the matcher, so passing the run's live
   * index would reset the sequence on every attack.
   */
  readonly startIndex?: number;
};

export function SequenceWatcher({
  groups,
  onGroupConfirmed,
  onSequenceCompleted,
  onError,
  onCurrentIndexChange,
  startIndex = 0,
}: SequenceWatcherProps): null {
  const { studio } = useStudio();

  // Recreate the matcher whenever the sequence reference changes. The
  // parent is in charge of stabilizing `groups` (typically with
  // `useMemo`) so we don't trash the matcher on every render.
  const matcher = useMemo(() => {
    const built = new SequenceMatcher(groups);
    built.seekTo(startIndex);

    return built;
  }, [groups, startIndex]);

  // Latch callbacks in refs so the subscription effect doesn't have to
  // depend on (and re-attach for) every parent re-render.
  const callbacksRef = useRef({
    onGroupConfirmed,
    onSequenceCompleted,
    onError,
    onCurrentIndexChange,
  });

  useEffect(() => {
    callbacksRef.current = {
      onGroupConfirmed,
      onSequenceCompleted,
      onError,
      onCurrentIndexChange,
    };
  }, [onGroupConfirmed, onSequenceCompleted, onError, onCurrentIndexChange]);

  // Notify the parent of the initial / reset index.
  useEffect(() => {
    callbacksRef.current.onCurrentIndexChange?.(matcher.getCurrentIndex());
  }, [matcher]);

  useEffect(() => {
    if (studio === null) {
      return;
    }

    const piano = studio.getPiano();
    const held = new Set<number>(piano.getPressedMidis().keys());

    const unsubscribe = piano.on((event) => {
      // Only what the player did. The application sounds notes on the same
      // piano — a chord preview, the hand it accompanies with — and without
      // this the watcher would take them for answers and walk the exercise
      // forward on its own.
      if (event.type !== "press" && event.type !== "release") {
        return;
      }

      if (event.origin !== "player") {
        return;
      }

      if (event.type === "release") {
        held.delete(event.note.toMidi());

        return;
      }

      const midi = event.note.toMidi();
      held.add(midi);
      const outcome = matcher.press(midi, held);

      if (outcome.kind === "wrongNote") {
        callbacksRef.current.onError?.(outcome.midi);

        return;
      }

      if (outcome.kind === "groupConfirmed") {
        callbacksRef.current.onGroupConfirmed?.(outcome.groupIndex);
        callbacksRef.current.onCurrentIndexChange?.(outcome.nextIndex);

        return;
      }

      if (outcome.kind === "sequenceCompleted") {
        callbacksRef.current.onGroupConfirmed?.(outcome.groupIndex);
        callbacksRef.current.onCurrentIndexChange?.(outcome.groupIndex + 1);
        callbacksRef.current.onSequenceCompleted?.();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [studio, matcher]);

  return null;
}

// ── Re-exports for convenience ────────────────────────────────────

export type { SequenceGroup };
